import { useMemo, useState } from "react";
import { calculate, type CalculatorInput, type CalculationKind, type CalculationResult, type SalaryFrequency, type SeparationReason, type WageZone } from "../engine";
import { ResultPanel } from "../components/ResultPanel";
import { CurrencyInput } from "../components/CurrencyInput";
import { useAuth } from "../auth/auth-context";
import { calculationsApi } from "../api/client";
import { APP_VERSION } from "../config";

type Mode = "basic" | "advanced";

interface FormState {
  kind: CalculationKind;
  mode: Mode;
  payDate: string;
  salaryAmount: string;
  frequency: SalaryFrequency;
  zone: WageZone;
  startDate: string;
  endDate: string;
  valuationDate: string;
  reason: SeparationReason;
  unpaidSalaryDays: string;
  unusedVacationDays: string;
  otherOrdinaryPayments: string;
  includeThreeMonths: boolean;
  includeTwentyDays: boolean;
  seniorityMode: "auto" | "yes" | "no";
  aguinaldoDays: string;
  vacationDays: string;
  vacationPremiumPct: string;
  overtimeHours: string;
  sundayShifts: string;
  restDays: string;
  otherTaxable: string;
  otherNonTaxable: string;
  infonavit: string;
  otherDeductions: string;
  riskPremiumPct: string;
  payrollTaxPct: string;
  state: string;
  saleDate: string;
  acquisitionDate: string;
  salePrice: string;
  ownershipPercentage: string;
  propertyUse: "house" | "other";
  houseUsePercentage: string;
  requestHouseExemption: boolean;
  formalizedBeforeNotary: boolean;
  claimedHouseExemptionLastThreeYears: boolean;
  acquisitionCost: string;
  landPercentage: string;
  improvementCost: string;
  improvementDate: string;
  acquisitionNotaryExpenses: string;
  acquisitionTaxesAndDuties: string;
  acquisitionCommissions: string;
  saleNotaryExpenses: string;
  appraisalExpenses: string;
  commissions: string;
  udiValue: string;
  salePreviousMonthInpc: string;
  acquisitionInpc: string;
  improvementInpc: string;
}

const today = new Date().toISOString().slice(0, 10);
const supportedToday =
  today >= "2024-01-01" && today <= "2026-12-31" ? today : "2026-07-28";

const initialState: FormState = {
  kind: "payroll",
  mode: "basic",
  payDate: supportedToday,
  salaryAmount: "",
  frequency: "monthly",
  zone: "general",
  startDate: "2025-01-01",
  endDate: supportedToday,
  valuationDate: supportedToday,
  reason: "resignation",
  unpaidSalaryDays: "0",
  unusedVacationDays: "0",
  otherOrdinaryPayments: "0",
  includeThreeMonths: true,
  includeTwentyDays: false,
  seniorityMode: "auto",
  aguinaldoDays: "15",
  vacationDays: "",
  vacationPremiumPct: "25",
  overtimeHours: "0",
  sundayShifts: "0",
  restDays: "0",
  otherTaxable: "0",
  otherNonTaxable: "0",
  infonavit: "0",
  otherDeductions: "0",
  riskPremiumPct: "0.54355",
  payrollTaxPct: "0",
  state: "",
  saleDate: supportedToday,
  acquisitionDate: "2018-01-01",
  salePrice: "",
  ownershipPercentage: "100",
  propertyUse: "house",
  houseUsePercentage: "100",
  requestHouseExemption: true,
  formalizedBeforeNotary: true,
  claimedHouseExemptionLastThreeYears: false,
  acquisitionCost: "",
  landPercentage: "20",
  improvementCost: "0",
  improvementDate: "",
  acquisitionNotaryExpenses: "0",
  acquisitionTaxesAndDuties: "0",
  acquisitionCommissions: "0",
  saleNotaryExpenses: "0",
  appraisalExpenses: "0",
  commissions: "0",
  udiValue: "",
  salePreviousMonthInpc: "",
  acquisitionInpc: "",
  improvementInpc: "",
};

const tabs: Array<{
  id: CalculationKind;
  label: string;
  description: string;
}> = [
  {
    id: "payroll",
    label: "Nómina",
    description: "Bruto, neto, ISR, IMSS y costo patronal.",
  },
  {
    id: "finiquito",
    label: "Finiquito",
    description: "Prestaciones devengadas al terminar.",
  },
  {
    id: "liquidation",
    label: "Liquidación",
    description: "Indemnizaciones en supuestos procedentes.",
  },
  {
    id: "litigation",
    label: "Escenario litigioso",
    description: "Contingencia por despido y duración.",
  },
  {
    id: "real-estate-isr",
    label: "ISR por enajenación de inmuebles",
    description: "Exención, costo actualizado, ganancia e ISR federal y estatal.",
  },
];

const frequencyLabels: Record<SalaryFrequency, string> = {
  daily: "Diario",
  weekly: "Semanal",
  fourteen: "Catorcenal",
  biweekly: "Quincenal",
  monthly: "Mensual",
};

const reasonOptions: Array<[SeparationReason, string]> = [
  ["resignation", "Renuncia voluntaria"],
  ["mutual", "Mutuo consentimiento"],
  ["justified-dismissal", "Despido justificado"],
  ["unjustified-dismissal", "Despido injustificado"],
  ["employee-rescission", "Rescisión justificada por la persona trabajadora"],
  ["death", "Fallecimiento"],
];

function numberValue(value: string): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="field">
      <span className="field__label">{label}</span>
      {children}
      {hint && <small>{hint}</small>}
    </label>
  );
}

function Toggle({
  checked,
  onChange,
  title,
  description,
}: {
  checked: boolean;
  onChange: (value: boolean) => void;
  title: string;
  description: string;
}) {
  return (
    <label className="toggle-card">
      <input
        type="checkbox"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
      />
      <span className="toggle-card__control" aria-hidden="true" />
      <span>
        <strong>{title}</strong>
        <small>{description}</small>
      </span>
    </label>
  );
}

export function CalculatorPage() {
  const duplicate = useMemo(() => {
    const serialized = sessionStorage.getItem("legalflow:duplicate");
    sessionStorage.removeItem("legalflow:duplicate");
    if (!serialized) return null;
    try {
      return JSON.parse(serialized) as CalculatorInput;
    } catch {
      return null;
    }
  }, []);
  const [form, setForm] = useState<FormState>(() => {
    if (!duplicate) return initialState;
    if (duplicate.kind === "real-estate-isr") {
      return {
        ...initialState,
        kind: duplicate.kind,
        saleDate: duplicate.saleDate,
        acquisitionDate: duplicate.acquisitionDate,
        salePrice: String(duplicate.salePrice),
        ownershipPercentage: String(duplicate.ownershipPercentage ?? 100),
        propertyUse: duplicate.propertyUse,
        houseUsePercentage: String(duplicate.houseUsePercentage ?? 100),
        requestHouseExemption: duplicate.requestHouseExemption !== false,
        formalizedBeforeNotary: duplicate.formalizedBeforeNotary !== false,
        claimedHouseExemptionLastThreeYears:
          duplicate.claimedHouseExemptionLastThreeYears === true,
        acquisitionCost: String(duplicate.acquisitionCost),
        landPercentage: String(duplicate.landPercentage ?? 20),
        improvementCost: String(duplicate.improvementCost ?? 0),
        improvementDate: duplicate.improvementDate ?? "",
        acquisitionNotaryExpenses: String(duplicate.acquisitionNotaryExpenses ?? 0),
        acquisitionTaxesAndDuties: String(duplicate.acquisitionTaxesAndDuties ?? 0),
        acquisitionCommissions: String(duplicate.acquisitionCommissions ?? 0),
        saleNotaryExpenses: String(duplicate.saleNotaryExpenses ?? 0),
        appraisalExpenses: String(duplicate.appraisalExpenses ?? 0),
        commissions: String(duplicate.commissions ?? 0),
        udiValue: duplicate.udiValue ? String(duplicate.udiValue) : "",
        salePreviousMonthInpc: duplicate.salePreviousMonthInpc
          ? String(duplicate.salePreviousMonthInpc)
          : "",
        acquisitionInpc: duplicate.acquisitionInpc
          ? String(duplicate.acquisitionInpc)
          : "",
        improvementInpc: duplicate.improvementInpc
          ? String(duplicate.improvementInpc)
          : "",
      };
    }
    const wage = duplicate.wage;
    return {
      ...initialState,
      kind: duplicate.kind,
      payDate: duplicate.payDate,
      salaryAmount: String(wage.amount),
      frequency: wage.frequency,
      zone: duplicate.zone,
      ...("startDate" in duplicate
        ? {
            startDate: duplicate.startDate,
            endDate: duplicate.endDate,
            reason: duplicate.reason,
          }
        : {}),
      ...("valuationDate" in duplicate
        ? { valuationDate: duplicate.valuationDate }
        : {}),
    };
  });
  const [result, setResult] = useState<CalculationResult | null>(null);
  const [input, setInput] = useState<CalculatorInput | null>(null);
  const [error, setError] = useState("");
  const [saveMessage, setSaveMessage] = useState("");
  const [saving, setSaving] = useState(false);
  const { user, signIn } = useAuth();

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((current) => ({ ...current, [key]: value }));

  const buildInput = (): CalculatorInput => {
    if (form.kind === "real-estate-isr") {
      return {
        kind: "real-estate-isr",
        saleDate: form.saleDate,
        acquisitionDate: form.acquisitionDate,
        salePrice: numberValue(form.salePrice),
        ownershipPercentage: numberValue(form.ownershipPercentage),
        propertyUse: form.propertyUse,
        houseUsePercentage: numberValue(form.houseUsePercentage),
        requestHouseExemption: form.requestHouseExemption,
        formalizedBeforeNotary: form.formalizedBeforeNotary,
        claimedHouseExemptionLastThreeYears:
          form.claimedHouseExemptionLastThreeYears,
        acquisitionCost: numberValue(form.acquisitionCost),
        landPercentage: numberValue(form.landPercentage),
        improvementCost: numberValue(form.improvementCost),
        improvementDate: form.improvementDate || undefined,
        acquisitionNotaryExpenses: numberValue(form.acquisitionNotaryExpenses),
        acquisitionTaxesAndDuties: numberValue(form.acquisitionTaxesAndDuties),
        acquisitionCommissions: numberValue(form.acquisitionCommissions),
        saleNotaryExpenses: numberValue(form.saleNotaryExpenses),
        appraisalExpenses: numberValue(form.appraisalExpenses),
        commissions: numberValue(form.commissions),
        udiValue: numberValue(form.udiValue) || undefined,
        salePreviousMonthInpc:
          numberValue(form.salePreviousMonthInpc) || undefined,
        acquisitionInpc: numberValue(form.acquisitionInpc) || undefined,
        improvementInpc: numberValue(form.improvementInpc) || undefined,
      };
    }
    const common = {
      payDate: form.payDate,
      wage: {
        amount: numberValue(form.salaryAmount),
        frequency: form.frequency,
      },
      zone: form.zone,
    };
    if (form.kind === "payroll") {
      return {
        kind: "payroll",
        ...common,
        overtimeHours: numberValue(form.overtimeHours),
        sundayShifts: numberValue(form.sundayShifts),
        holidayOrRestDays: numberValue(form.restDays),
        otherTaxableIncome: numberValue(form.otherTaxable),
        otherNonTaxableIncome: numberValue(form.otherNonTaxable),
        infonavitWithholding: numberValue(form.infonavit),
        otherDeductions: numberValue(form.otherDeductions),
        aguinaldoDays: numberValue(form.aguinaldoDays),
        vacationDaysPerYear: numberValue(form.vacationDays) || undefined,
        vacationPremiumRate: numberValue(form.vacationPremiumPct) / 100,
        riskPremiumRate: numberValue(form.riskPremiumPct),
        payrollTaxRate: numberValue(form.payrollTaxPct),
        state: form.state || undefined,
      };
    }

    const separation = {
      ...common,
      startDate: form.startDate,
      endDate: form.endDate,
      reason: form.reason,
      unpaidSalaryDays: numberValue(form.unpaidSalaryDays),
      unusedVacationDays: numberValue(form.unusedVacationDays),
      otherOrdinaryPayments: numberValue(form.otherOrdinaryPayments),
      includeThreeMonths: form.includeThreeMonths,
      includeTwentyDaysPerYear: form.includeTwentyDays,
      includeSeniorityPremium:
        form.seniorityMode === "auto"
          ? undefined
          : form.seniorityMode === "yes",
      aguinaldoDays: numberValue(form.aguinaldoDays),
      vacationDaysPerYear: numberValue(form.vacationDays) || undefined,
      vacationPremiumRate: numberValue(form.vacationPremiumPct) / 100,
    };

    if (form.kind === "litigation") {
      return {
        ...separation,
        kind: "litigation",
        reason:
          form.reason === "employee-rescission"
            ? "employee-rescission"
            : "unjustified-dismissal",
        valuationDate: form.valuationDate,
      };
    }
    return { ...separation, kind: form.kind };
  };

  const handleCalculate = (event: React.FormEvent) => {
    event.preventDefault();
    setError("");
    setSaveMessage("");
    try {
      const nextInput = buildInput();
      const nextResult = calculate(nextInput);
      setInput(nextInput);
      setResult(nextResult);
      window.setTimeout(
        () => document.getElementById("resultado")?.scrollIntoView({ behavior: "smooth" }),
        0,
      );
    } catch (caught) {
      setResult(null);
      setError(
        caught instanceof Error
          ? caught.message
          : "No fue posible realizar el cálculo.",
      );
    }
  };

  const handleSave = async () => {
    if (!user) {
      await signIn();
      return;
    }
    if (!result || !input) return;
    setSaving(true);
    setSaveMessage("");
    try {
      await calculationsApi.create(user.access_token, {
        title: `${result.title} · ${new Intl.DateTimeFormat("es-MX").format(new Date())}`,
        kind: result.kind,
        input,
        result,
      });
      setSaveMessage("Cálculo guardado en tu cuenta.");
    } catch (caught) {
      setSaveMessage(
        caught instanceof Error ? caught.message : "No fue posible guardar.",
      );
    } finally {
      setSaving(false);
    }
  };

  const isFiscal = form.kind === "real-estate-isr";
  const isSeparation =
    form.kind === "finiquito" ||
    form.kind === "liquidation" ||
    form.kind === "litigation";
  const isLiquidation =
    form.kind === "liquidation" || form.kind === "litigation";

  return (
    <>
      <section className="hero">
        <div className="hero__inner">
          <div>
            <div className="hero__meta">
              <span className="eyebrow">México · Matriz normativa 2024–2026</span>
              <span className="version-badge" aria-label={`Versión actual ${APP_VERSION}`}>
                <span className="version-badge__dot" aria-hidden="true" />
                <span>Versión actual</span>
                <strong>{APP_VERSION}</strong>
              </span>
            </div>
            <h1>Decisiones laborales y fiscales con números claros.</h1>
            <p>
              Estima nómina, terminaciones laborales e ISR por enajenación de
              inmuebles con desglose normativo. Gratis, sin registro obligatorio.
            </p>
          </div>
          <aside className="trust-card">
            <span className="trust-card__icon" aria-hidden="true">✓</span>
            <div>
              <strong>Privacidad desde el diseño</strong>
              <p>
                Calcula sin cuenta. Sólo guardamos información cuando tú lo
                eliges.
              </p>
            </div>
          </aside>
        </div>
      </section>

      <div className="calculator-layout">
        <section className="calculator-card">
          <header className="calculator-card__header">
            <div>
              <span className="eyebrow">Selecciona un cálculo</span>
              <h2>¿Qué necesitas estimar?</h2>
            </div>
            <div className="mode-switch" aria-label="Nivel de detalle">
              <button
                className={form.mode === "basic" ? "active" : ""}
                onClick={() => set("mode", "basic")}
                type="button"
              >
                Básico
              </button>
              <button
                className={form.mode === "advanced" ? "active" : ""}
                onClick={() => set("mode", "advanced")}
                type="button"
              >
                Avanzado
              </button>
            </div>
          </header>

          <div className="calculation-families" aria-label="Familia de cálculo">
            <button
              type="button"
              className={!isFiscal ? "active" : ""}
              aria-pressed={!isFiscal}
              onClick={() => {
                set("kind", "payroll");
                setResult(null);
              }}
            >
              <span>Laboral</span>
              <small>Nómina, finiquito, liquidación y litigio</small>
            </button>
            <button
              type="button"
              className={isFiscal ? "active" : ""}
              aria-pressed={isFiscal}
              onClick={() => {
                set("kind", "real-estate-isr");
                setResult(null);
              }}
            >
              <span>Fiscal</span>
              <small>Impuestos de operaciones y patrimonio</small>
            </button>
          </div>

          <div
            className={`calculation-tabs ${isFiscal ? "calculation-tabs--single" : ""}`}
            role="tablist"
          >
            {tabs.filter((tab) =>
              isFiscal ? tab.id === "real-estate-isr" : tab.id !== "real-estate-isr",
            ).map((tab) => (
              <button
                type="button"
                role="tab"
                aria-selected={form.kind === tab.id}
                className={form.kind === tab.id ? "active" : ""}
                key={tab.id}
                onClick={() => {
                  set("kind", tab.id);
                  if (tab.id === "litigation") {
                    set("reason", "unjustified-dismissal");
                  }
                  setResult(null);
                }}
              >
                <strong>{tab.label}</strong>
                <small>{tab.description}</small>
              </button>
            ))}
          </div>

          <form onSubmit={handleCalculate} className="calculator-form">
            {isFiscal ? (
              <>
                <fieldset>
                  <legend>Operación y participación</legend>
                  <p className="fieldset-intro">
                    Captura los importes totales del inmueble. La calculadora aplica
                    automáticamente la participación de la persona enajenante.
                  </p>
                  <div className="form-grid">
                    <Field label="Precio de venta total">
                      <CurrencyInput
                        required
                        value={form.salePrice}
                        onChange={(value) => set("salePrice", value)}
                        placeholder="5,000,000.00"
                      />
                    </Field>
                    <Field
                      label="Participación del enajenante"
                      hint="Ejemplo: 50% si el inmueble pertenece por mitades a dos personas."
                    >
                      <div className="suffix-input">
                        <input
                          required
                          min="0.01"
                          max="100"
                          step="0.01"
                          type="number"
                          value={form.ownershipPercentage}
                          onChange={(event) =>
                            set("ownershipPercentage", event.target.value)
                          }
                        />
                        <span>%</span>
                      </div>
                    </Field>
                    <Field label="Fecha de adquisición">
                      <input
                        required
                        type="date"
                        value={form.acquisitionDate}
                        onChange={(event) =>
                          set("acquisitionDate", event.target.value)
                        }
                      />
                    </Field>
                    <Field
                      label="Fecha de venta"
                      hint="Determina la UDI, el INPC y la tarifa aplicable."
                    >
                      <input
                        required
                        type="date"
                        min="2024-01-01"
                        max="2026-12-31"
                        value={form.saleDate}
                        onChange={(event) => set("saleDate", event.target.value)}
                      />
                    </Field>
                  </div>
                </fieldset>

                <fieldset>
                  <legend>Inmueble y exención</legend>
                  <div className="form-grid">
                    <Field label="Uso del inmueble">
                      <select
                        value={form.propertyUse}
                        onChange={(event) =>
                          set(
                            "propertyUse",
                            event.target.value as FormState["propertyUse"],
                          )
                        }
                      >
                        <option value="house">Casa habitación</option>
                        <option value="other">Otro inmueble</option>
                      </select>
                    </Field>
                    {form.propertyUse === "house" && (
                      <Field
                        label="Porción destinada a casa habitación"
                        hint="Reduce este porcentaje si una parte tuvo un uso distinto."
                      >
                        <div className="suffix-input">
                          <input
                            min="0"
                            max="100"
                            step="0.01"
                            type="number"
                            value={form.houseUsePercentage}
                            onChange={(event) =>
                              set("houseUsePercentage", event.target.value)
                            }
                          />
                          <span>%</span>
                        </div>
                      </Field>
                    )}
                  </div>
                  {form.propertyUse === "house" && (
                    <div className="toggle-grid toggle-grid--stacked">
                      <Toggle
                        checked={form.requestHouseExemption}
                        onChange={(value) => set("requestHouseExemption", value)}
                        title="Aplicar exención de casa habitación"
                        description="Hasta 700,000 UDI por persona enajenante, sujeto a requisitos."
                      />
                      <Toggle
                        checked={form.formalizedBeforeNotary}
                        onChange={(value) => set("formalizedBeforeNotary", value)}
                        title="La venta se formaliza ante fedatario"
                        description="Es requisito para aplicar la exención en esta operación."
                      />
                      <Toggle
                        checked={form.claimedHouseExemptionLastThreeYears}
                        onChange={(value) =>
                          set("claimedHouseExemptionLastThreeYears", value)
                        }
                        title="Se usó esta exención en los últimos tres años"
                        description="Actívalo si la persona ya exentó otra casa habitación en ese periodo."
                      />
                    </div>
                  )}
                </fieldset>

                <fieldset>
                  <legend>Costo comprobado</legend>
                  <div className="form-grid">
                    <Field
                      label="Costo de adquisición total"
                      hint="Precio consignado en la escritura o comprobante de adquisición."
                    >
                      <CurrencyInput
                        required
                        value={form.acquisitionCost}
                        onChange={(value) => set("acquisitionCost", value)}
                        placeholder="2,000,000.00"
                      />
                    </Field>
                  </div>
                </fieldset>

                {form.mode === "advanced" && (
                  <>
                    <fieldset className="advanced-fields">
                      <legend>Deducciones autorizadas</legend>
                      <p className="fieldset-intro">
                        Incluye únicamente importes comprobables. Se actualizan y
                        prorratean conforme a la parte no exenta.
                      </p>
                      <div className="form-grid">
                        <Field
                          label="Porción del costo atribuible al terreno"
                          hint="Si la escritura no separa valores, la ley permite considerar 20%."
                        >
                          <div className="suffix-input">
                            <input
                              min="0"
                              max="100"
                              step="0.01"
                              type="number"
                              value={form.landPercentage}
                              onChange={(event) =>
                                set("landPercentage", event.target.value)
                              }
                            />
                            <span>%</span>
                          </div>
                        </Field>
                        <Field label="Construcciones, mejoras y ampliaciones">
                          <CurrencyInput
                            value={form.improvementCost}
                            onChange={(value) => set("improvementCost", value)}
                          />
                        </Field>
                        {numberValue(form.improvementCost) > 0 && (
                          <Field label="Fecha de las mejoras">
                            <input
                              required
                              type="date"
                              value={form.improvementDate}
                              onChange={(event) =>
                                set("improvementDate", event.target.value)
                              }
                            />
                          </Field>
                        )}
                        {([
                          ["acquisitionNotaryExpenses", "Gastos notariales de adquisición"],
                          ["acquisitionTaxesAndDuties", "Impuestos y derechos de adquisición"],
                          ["acquisitionCommissions", "Comisiones por la adquisición"],
                          ["saleNotaryExpenses", "Gastos notariales de venta"],
                          ["appraisalExpenses", "Avalúos"],
                          ["commissions", "Comisiones y mediaciones por la venta"],
                        ] as const).map(([key, label]) => (
                          <Field label={label} key={key}>
                            <CurrencyInput
                              value={form[key]}
                              onChange={(value) => set(key, value)}
                            />
                          </Field>
                        ))}
                      </div>
                    </fieldset>

                    <fieldset className="advanced-fields">
                      <legend>Indicadores fiscales</legend>
                      <p className="fieldset-intro">
                        Déjalos vacíos para usar los valores oficiales integrados.
                        Captura un valor sólo si necesitas reproducir otra fecha o fuente.
                      </p>
                      <div className="form-grid">
                        <Field label="UDI en la fecha de venta">
                          <input
                            min="0"
                            step="0.000001"
                            type="number"
                            value={form.udiValue}
                            onChange={(event) => set("udiValue", event.target.value)}
                            placeholder="Automática"
                          />
                        </Field>
                        <Field label="INPC del mes anterior a la venta">
                          <input
                            min="0"
                            step="0.00001"
                            type="number"
                            value={form.salePreviousMonthInpc}
                            onChange={(event) =>
                              set("salePreviousMonthInpc", event.target.value)
                            }
                            placeholder="Automático"
                          />
                        </Field>
                        <Field label="INPC del mes de adquisición">
                          <input
                            min="0"
                            step="0.00001"
                            type="number"
                            value={form.acquisitionInpc}
                            onChange={(event) =>
                              set("acquisitionInpc", event.target.value)
                            }
                            placeholder="Automático"
                          />
                        </Field>
                        {numberValue(form.improvementCost) > 0 && (
                          <Field label="INPC del mes de las mejoras">
                            <input
                              min="0"
                              step="0.00001"
                              type="number"
                              value={form.improvementInpc}
                              onChange={(event) =>
                                set("improvementInpc", event.target.value)
                              }
                              placeholder="Automático"
                            />
                          </Field>
                        )}
                      </div>
                    </fieldset>
                  </>
                )}
              </>
            ) : (
              <>
            <fieldset>
              <legend>Salario y periodo</legend>
              <div className="form-grid">
                <Field label="Salario bruto">
                  <CurrencyInput
                    required
                    value={form.salaryAmount}
                    onChange={(value) => set("salaryAmount", value)}
                    placeholder="30,000.00"
                  />
                </Field>
                <Field label="Periodicidad">
                  <select
                    value={form.frequency}
                    onChange={(event) =>
                      set("frequency", event.target.value as SalaryFrequency)
                    }
                  >
                    {Object.entries(frequencyLabels).map(([value, label]) => (
                      <option value={value} key={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field
                  label="Zona salarial"
                  hint="Sólo afecta mínimos y topes vinculados al salario mínimo."
                >
                  <select
                    value={form.zone}
                    onChange={(event) =>
                      set("zone", event.target.value as WageZone)
                    }
                  >
                    <option value="general">Resto del país</option>
                    <option value="border">Zona Libre de la Frontera Norte</option>
                  </select>
                </Field>
                <Field label="Fecha de pago">
                  <input
                    required
                    type="date"
                    min="2024-01-01"
                    max="2026-12-31"
                    value={form.payDate}
                    onChange={(event) => set("payDate", event.target.value)}
                  />
                </Field>
              </div>
            </fieldset>

            {isSeparation && (
              <fieldset>
                <legend>Relación de trabajo</legend>
                <div className="form-grid">
                  <Field label="Fecha de ingreso">
                    <input
                      required
                      type="date"
                      value={form.startDate}
                      onChange={(event) => set("startDate", event.target.value)}
                    />
                  </Field>
                  <Field label="Fecha de baja o despido">
                    <input
                      required
                      type="date"
                      value={form.endDate}
                      onChange={(event) => set("endDate", event.target.value)}
                    />
                  </Field>
                  {form.kind !== "litigation" && (
                    <Field label="Motivo de terminación">
                      <select
                        value={form.reason}
                        onChange={(event) =>
                          set("reason", event.target.value as SeparationReason)
                        }
                      >
                        {reasonOptions.map(([value, label]) => (
                          <option key={value} value={value}>
                            {label}
                          </option>
                        ))}
                      </select>
                    </Field>
                  )}
                  {form.kind === "litigation" && (
                    <Field
                      label="Fecha de valuación"
                      hint="Día al que quieres proyectar la contingencia."
                    >
                      <input
                        required
                        type="date"
                        value={form.valuationDate}
                        onChange={(event) =>
                          set("valuationDate", event.target.value)
                        }
                      />
                    </Field>
                  )}
                  <Field label="Días de salario pendientes">
                    <input
                      min="0"
                      step="0.01"
                      type="number"
                      value={form.unpaidSalaryDays}
                      onChange={(event) =>
                        set("unpaidSalaryDays", event.target.value)
                      }
                    />
                  </Field>
                  <Field
                    label="Vacaciones ya devengadas y no gozadas"
                    hint="Días pendientes de periodos completos; la proporción actual es automática."
                  >
                    <input
                      min="0"
                      step="0.01"
                      type="number"
                      value={form.unusedVacationDays}
                      onChange={(event) =>
                        set("unusedVacationDays", event.target.value)
                      }
                    />
                  </Field>
                </div>
              </fieldset>
            )}

            {isLiquidation && (
              <fieldset>
                <legend>Indemnizaciones a modelar</legend>
                <div className="toggle-grid">
                  <Toggle
                    checked={form.includeThreeMonths}
                    onChange={(value) => set("includeThreeMonths", value)}
                    title="Tres meses"
                    description="Indemnización constitucional en los supuestos seleccionados."
                  />
                  <Toggle
                    checked={form.includeTwentyDays}
                    onChange={(value) => set("includeTwentyDays", value)}
                    title="Veinte días por año"
                    description="Actívalo sólo si la acción o convenio lo hace procedente."
                  />
                </div>
              </fieldset>
            )}

            {form.kind === "payroll" && (
              <fieldset>
                <legend>Incidencias del periodo</legend>
                <div className="form-grid">
                  <Field label="Horas extra en la semana">
                    <input
                      min="0"
                      step="0.25"
                      type="number"
                      value={form.overtimeHours}
                      onChange={(event) => set("overtimeHours", event.target.value)}
                    />
                  </Field>
                  <Field label="Domingos laborados">
                    <input
                      min="0"
                      step="1"
                      type="number"
                      value={form.sundayShifts}
                      onChange={(event) => set("sundayShifts", event.target.value)}
                    />
                  </Field>
                  <Field label="Descansos obligatorios laborados">
                    <input
                      min="0"
                      step="1"
                      type="number"
                      value={form.restDays}
                      onChange={(event) => set("restDays", event.target.value)}
                    />
                  </Field>
                </div>
              </fieldset>
            )}

            {form.mode === "advanced" && (
              <fieldset className="advanced-fields">
                <legend>Prestaciones y ajustes avanzados</legend>
                <div className="form-grid">
                  <Field label="Días de aguinaldo al año">
                    <input
                      min="15"
                      step="1"
                      type="number"
                      value={form.aguinaldoDays}
                      onChange={(event) => set("aguinaldoDays", event.target.value)}
                    />
                  </Field>
                  <Field
                    label="Días de vacaciones al año"
                    hint="Déjalo vacío para aplicar la tabla legal por antigüedad."
                  >
                    <input
                      min="12"
                      step="1"
                      type="number"
                      value={form.vacationDays}
                      onChange={(event) => set("vacationDays", event.target.value)}
                    />
                  </Field>
                  <Field label="Prima vacacional">
                    <div className="suffix-input">
                      <input
                        min="25"
                        step="0.01"
                        type="number"
                        value={form.vacationPremiumPct}
                        onChange={(event) =>
                          set("vacationPremiumPct", event.target.value)
                        }
                      />
                      <span>%</span>
                    </div>
                  </Field>
                  {form.kind === "payroll" ? (
                    <>
                      <Field
                        label="Otras percepciones gravadas"
                        hint="Si son fijas e integrables al SBC, valida su integración bimestral por separado."
                      >
                        <CurrencyInput
                          value={form.otherTaxable}
                          onChange={(value) => set("otherTaxable", value)}
                        />
                      </Field>
                      <Field label="Otras percepciones exentas capturadas">
                        <CurrencyInput
                          value={form.otherNonTaxable}
                          onChange={(value) => set("otherNonTaxable", value)}
                        />
                      </Field>
                      <Field label="Retención INFONAVIT">
                        <CurrencyInput
                          value={form.infonavit}
                          onChange={(value) => set("infonavit", value)}
                        />
                      </Field>
                      <Field label="Otras deducciones">
                        <CurrencyInput
                          value={form.otherDeductions}
                          onChange={(value) => set("otherDeductions", value)}
                        />
                      </Field>
                      <Field label="Prima de riesgo de trabajo">
                        <div className="suffix-input">
                          <input
                            min="0.5"
                            max="15"
                            step="0.00001"
                            type="number"
                            value={form.riskPremiumPct}
                            onChange={(event) =>
                              set("riskPremiumPct", event.target.value)
                            }
                          />
                          <span>%</span>
                        </div>
                      </Field>
                      <Field
                        label="Entidad para impuesto sobre nóminas"
                        hint="El nombre sólo se muestra en el desglose; no se envía al servidor al calcular."
                      >
                        <input
                          maxLength={60}
                          type="text"
                          value={form.state}
                          onChange={(event) => set("state", event.target.value)}
                          placeholder="Ej. Ciudad de México"
                        />
                      </Field>
                      <Field
                        label="Tasa estatal sobre nóminas"
                        hint="Consulta la tasa y exenciones vigentes en la entidad."
                      >
                        <div className="suffix-input">
                          <input
                            min="0"
                            max="10"
                            step="0.01"
                            type="number"
                            value={form.payrollTaxPct}
                            onChange={(event) =>
                              set("payrollTaxPct", event.target.value)
                            }
                          />
                          <span>%</span>
                        </div>
                      </Field>
                    </>
                  ) : (
                    <>
                      <Field label="Otros pagos ordinarios pendientes">
                        <CurrencyInput
                          value={form.otherOrdinaryPayments}
                          onChange={(value) =>
                            set("otherOrdinaryPayments", value)
                          }
                        />
                      </Field>
                      <Field label="Prima de antigüedad">
                        <select
                          value={form.seniorityMode}
                          onChange={(event) =>
                            set(
                              "seniorityMode",
                              event.target.value as FormState["seniorityMode"],
                            )
                          }
                        >
                          <option value="auto">Determinar legalmente</option>
                          <option value="yes">Incluir</option>
                          <option value="no">Excluir</option>
                        </select>
                      </Field>
                    </>
                  )}
                </div>
              </fieldset>
            )}
              </>
            )}

            {error && <div className="form-error">{error}</div>}
            <div className="form-submit">
              <div>
                <strong>Cálculo local y privado</strong>
                <small>
                  No enviamos los datos al servidor salvo que elijas guardar.
                </small>
              </div>
              <button className="button button--gold button--large" type="submit">
                Calcular ahora
              </button>
            </div>
          </form>
        </section>

        <aside className="calculator-aside">
          <div className="aside-card">
            <span className="eyebrow">Qué obtendrás</span>
            <ul className="check-list">
              {isFiscal ? (
                <>
                  <li>Exención de hasta 700,000 UDI</li>
                  <li>Costos y gastos actualizados</li>
                  <li>Ganancia gravable por año</li>
                  <li>ISR federal y pago estatal</li>
                  <li>Fundamento y fuentes oficiales</li>
                </>
              ) : (
                <>
                  <li>Desglose por concepto</li>
                  <li>Parte gravada y exenta</li>
                  <li>Fundamento legal visible</li>
                  <li>Supuestos y alertas</li>
                  <li>Versión normativa reproducible</li>
                </>
              )}
            </ul>
          </div>
          <div className="aside-card aside-card--muted">
            <strong>Precisión condicionada</strong>
            <p>
              {isFiscal
                ? "Para escriturar, valida documentos, fechas y deducciones con el fedatario que formalice la operación."
                : "Para decisiones reales, usa el modo avanzado y captura prestaciones contractuales, pendientes e incidencias completas."}
            </p>
          </div>
        </aside>
      </div>

      {result && (
        <div className="result-wrap">
          {saveMessage && <p className="save-message">{saveMessage}</p>}
          <ResultPanel
            result={result}
            saving={saving}
            onSave={() => void handleSave()}
          />
        </div>
      )}
    </>
  );
}
